/**
 * useInViewport
 * 
 * A hook that returns true if an element is in the viewport.
 * 
 * @param {React.RefObject} ref - The ref of the element to check.
 * @param {Function} onInView - The function to call when the element is in the viewport.
 * @param {Object} options - The options for the hook.
 * @param {number} options.threshold - The threshold for the element to be considered in the viewport, default is 0.25.
 * @param {boolean} options.triggerOnce - Whether the element should only be considered in the viewport once, default is false.
 */

import { useState, useEffect, useRef } from "react";

const useInViewport = (
    ref,
    onInView = () => {},
    {
      threshold = 0.25,
      triggerOnce = false
    } = {}
  ) => {
    const [isInView, setIsInView] = useState(false);
    const hasTriggered = useRef(false);
  
    useEffect(() => {
      if (!ref.current) return;
  
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const isVisible = entry.intersectionRatio >= threshold;
            
            if (isVisible && (!triggerOnce || !hasTriggered.current)) {
              setIsInView(true);
              hasTriggered.current = true;

              typeof onInView === 'function' && onInView();
              
              // Disconnect observer if triggerOnce is true
              if (triggerOnce) {
                observer.disconnect();
              }
            } else {
              setIsInView(false);
            }
          });
        },
        {
          threshold: threshold,
          root: null,
          rootMargin: '0px'
        }
      );
  
      observer.observe(ref.current);
  
      return () => {
        observer.disconnect();
      };
    }, [ref, threshold, triggerOnce, onInView]);

    return isInView;
};

export default useInViewport;